/**
 * ProfileContext.tsx - React Context for Profylr
 * 
 * CHANGE HISTORY:
 * - 251102_1441 danielbeer: Initial ProfileContext implementation
 */

import React, { createContext, useContext, useEffect, useState, useMemo, ReactNode } from 'react';
import { ProfileManager, getProfylr } from './ProfileManager';
import type { Profile, ResolvedProfile, ProfileQueryOptions } from './types';

interface ProfileContextValue {
  profylr: ProfileManager;
  activeProfile: ResolvedProfile | null;
  profiles: Profile[];
  loading: boolean;
  error: string | null;
  
  // Actions
  activate: (profileId: string) => Promise<void>;
  create: (profileData: Omit<Profile, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Profile>;
  update: (profileId: string, updates: Partial<Profile>) => Promise<Profile>;
  delete: (profileId: string) => Promise<boolean>;
  list: (options?: ProfileQueryOptions) => Promise<Profile[]>;
  refresh: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

export interface ProfylrProviderProps {
  children: ReactNode;
  actr?: any;
  aggregatr?: any;
  autoLoadProfiles?: boolean;
}

export const ProfylrProvider: React.FC<ProfylrProviderProps> = ({
  children,
  actr,
  aggregatr,
  autoLoadProfiles = true,
}) => {
  const [profylr] = useState(() => getProfylr({ actr, aggregatr }));
  const [activeProfile, setActiveProfile] = useState<ResolvedProfile | null>(null);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Load profiles on mount
  useEffect(() => {
    if (autoLoadProfiles) {
      loadProfiles();
    } else {
      setLoading(false);
    }
  }, [autoLoadProfiles]);
  
  // Load active profile when it changes
  useEffect(() => {
    loadActiveProfile();
  }, [profylr]);
  
  const loadProfiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const loadedProfiles = await profylr.list();
      setProfiles(loadedProfiles);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };
  
  const loadActiveProfile = async () => {
    try {
      const active = await profylr.getActiveProfile();
      setActiveProfile(active);
    } catch (err) {
      console.error('Failed to load active profile:', err);
    }
  };
  
  const activate = async (profileId: string) => {
    try {
      setError(null);
      await profylr.activate(profileId);
      await loadActiveProfile();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate profile');
      throw err;
    }
  };
  
  const create = async (profileData: Omit<Profile, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      setError(null);
      const profile = await profylr.create(profileData);
      await loadProfiles();
      return profile;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create profile');
      throw err;
    }
  };
  
  const update = async (profileId: string, updates: Partial<Profile>) => {
    try {
      setError(null);
      const profile = await profylr.update(profileId, updates);
      await loadProfiles();
      if (activeProfile?.id === profileId) {
        await loadActiveProfile();
      }
      return profile;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
      throw err;
    }
  };
  
  const deleteProfile = async (profileId: string) => {
    try {
      setError(null);
      const result = await profylr.delete(profileId);
      await loadProfiles();
      if (activeProfile?.id === profileId) {
        setActiveProfile(null);
      }
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete profile');
      throw err;
    }
  };
  
  const list = async (options?: ProfileQueryOptions) => {
    try {
      setError(null);
      return await profylr.list(options);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to list profiles');
      throw err;
    }
  };
  
  const refresh = async () => {
    await loadProfiles();
    await loadActiveProfile();
  };
  
  // Memoize context value to prevent infinite re-renders
  const value = useMemo<ProfileContextValue>(() => ({
    profylr,
    activeProfile,
    profiles,
    loading,
    error,
    activate,
    create,
    update,
    delete: deleteProfile,
    list,
    refresh,
  }), [profylr, activeProfile, profiles, loading, error, activate, create, update, deleteProfile, list, refresh]);
  
  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};

/**
 * Hook to use Profylr context
 */
export function useProfylr(): ProfileContextValue {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfylr must be used within a ProfileProvider');
  }
  return context;
}
